import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';

import { InputGroup, Switch, ToggleButtonGroup } from 'core/form';
import storeLoader from 'app/stores/storeLoader';
import {
  MITIGATION_UI_STATE_TO_API_STATES_MAP,
  MITIGATION_MANUAL_UI_STATES
} from '@kentik/ui-shared/mitigations/constants';
import AdminFilterSidebar from 'app/components/admin/AdminFilterSidebar';
import { timezone } from 'core/util/dateUtils';
import moment from 'moment';

const DAY_IN_SECONDS = 86400;
const ALL_TIME_LOOKBACK = -1;

// Inactives are included if array is missing, empty, or includes 'Inactive'
const includesInactive = (states = []) => states?.length === 0 || states.includes('Inactive');

@inject('$mkp', '$alerting', '$app', '$exports')
@storeLoader('$mitigations.platformsCollection', '$mitigations.methodsCollection', '$alerting._flowPolicyCollection')
@withRouter
@observer
class MitigationFilterSidebar extends Component {
  static defaultProps = {
    lookbackOnly: false,
    readOnly: false,
    filterValues: undefined,
    tenantMethodOptions: [],
    tenantPlatformOptions: [],
    updateHashOnFilterChange: true,
    initialMitigationId: null
  };

  state = { tenantMethodOptions: [], tenantPlatformOptions: [], showTenants: false };

  componentDidMount() {
    const { $app, $exports, $mitigations } = this.props;

    if ($app.isSubtenant) {
      // attempt to load mitigations for the last 90 days then extract the methods and platforms that were used
      $mitigations.allUsedPlatformsAndMethods.then((options) => {
        this.setState({ tenantMethodOptions: options.methodOptions, tenantPlatformOptions: options.platformOptions });
      });
    }

    $exports.getSettings().then(({ hashedFilters }) => {
      if (hashedFilters) {
        const { startDate, endDate, ...restHashedFilters } = hashedFilters;

        // Make sure we convert hashed dates into moment objects
        if (typeof startDate === 'string') {
          restHashedFilters.startDate = moment(startDate);
        }
        if (typeof endDate === 'string') {
          restHashedFilters.endDate = moment(endDate);
        }

        // Prefer lookback if we have it
        if (restHashedFilters.lookback > 0) {
          restHashedFilters.startDate = moment().subtract(restHashedFilters.lookback, 'seconds');
          restHashedFilters.endDate = moment();
        }

        if (restHashedFilters.lookback === ALL_TIME_LOOKBACK && includesInactive(restHashedFilters.statuses)) {
          restHashedFilters.lookback = DAY_IN_SECONDS; // set to last 24hrs if inactives are included
        }

        this.handleFilterChange(restHashedFilters);
      }
    });
  }

  // Initial state
  get defaultValues() {
    const { initialMitigationId } = this.props;

    return {
      types: [],
      policies: [],
      mitigations: initialMitigationId || '',
      methodID: [],
      platformID: [],
      statuses: initialMitigationId ? ['Waiting', 'Failed', 'Active', 'Inactive'] : ['Waiting', 'Failed', 'Active'],
      sources: [],
      control: [],
      tenantID: [],
      lookback: initialMitigationId ? DAY_IN_SECONDS * 90 : ALL_TIME_LOOKBACK,
      alarms: '',
      ipCidrPattern: '',
      keyPartsSearch: '',
      exactSearch: false,
      showTenants: false
    };
  }

  // Cleared state
  get resetValues() {
    return {
      ...this.defaultValues
    };
  }

  get filterFields() {
    const { $app, $mitigations, $mkp, $alerting, lookbackOnly, filterValues } = this.props;
    const { tenantMethodOptions, tenantPlatformOptions, showTenants } = this.state;
    let methodOptions;
    let platformOptions;

    // set available method options - tenants should only see what they been assigned
    if (!$app.isSubtenant) {
      // not a tenant - show all defined methods and platforms
      methodOptions = $mitigations.methodsCollection.mitigationOptions;
      platformOptions = $mitigations.platformsCollection.generateSelectOptions({ labelKey: 'name', valueKey: 'id' });
    } else if (tenantMethodOptions?.length > 0) {
      // tenant - show methods and platforms used in the last 90 days (if loaded)
      methodOptions = tenantMethodOptions;
      platformOptions = tenantPlatformOptions;
    } else {
      // tenant - default to showing the methods and platforms used in the currently loaded collection
      methodOptions = $mitigations.collection.platformMethodOptions.methodOptions;
      platformOptions = $mitigations.collection.platformMethodOptions.platformOptions;
    }

    const filterFields = {
      lookback: {
        name: 'lookback',
        label: 'Time Range',
        defaultValue: this.defaultValues.lookback,
        hidden: !lookbackOnly,
        options: [
          { label: 'Last 24 hours', value: DAY_IN_SECONDS },
          { label: 'Last 7 days', value: DAY_IN_SECONDS * 7 },
          { label: 'Last 14 days', value: DAY_IN_SECONDS * 14 },
          { label: 'Last 30 days', value: DAY_IN_SECONDS * 30 },
          { label: 'Last 90 days', value: DAY_IN_SECONDS * 90 },
          { label: 'All time', value: ALL_TIME_LOOKBACK }
        ]
      },

      daterange: {
        name: 'daterange',
        label: `Time Range (${timezone.value})`,
        isDateRange: true,
        closeDateRangeOnSelection: true,
        startField: 'startDate',
        endField: 'endDate',
        asStrings: true,
        hidden: lookbackOnly,
        startDefaultValue: null,
        endDefaultValue: null,
        adjustEndTime: true,
        showShortcutLabelAsInterval: true,
        overrideShortcuts: [
          { label: 'Last hour', lookbackSeconds: DAY_IN_SECONDS / 24, includeTime: true },
          { label: 'Last 8 hours', lookbackSeconds: DAY_IN_SECONDS / 3, includeTime: true },
          { label: 'Last 24 hours', lookbackSeconds: DAY_IN_SECONDS, includeTime: true },
          { label: 'Last 7 days', lookbackSeconds: DAY_IN_SECONDS * 7, includeTime: true },
          { label: 'Last 14 days', lookbackSeconds: DAY_IN_SECONDS * 14, includeTime: true },
          { label: 'Last 30 days', lookbackSeconds: DAY_IN_SECONDS * 30, includeTime: true },
          { label: 'Last 90 days', lookbackSeconds: DAY_IN_SECONDS * 90, includeTime: true },
          {
            label: 'All time ',
            lookbackSeconds: ALL_TIME_LOOKBACK,
            isKeyword: true
          }
        ]
      },

      statuses: {
        name: 'statuses',
        label: 'Status',
        allowMultiSelect: true,
        showCheckboxes: true,
        defaultValue: this.defaultValues.statuses,
        options: [
          { label: 'Active', value: 'Active', color: $mitigations.getMitigationStatusColor('Active'), round: true },
          { label: 'Failed', value: 'Failed', color: $mitigations.getMitigationStatusColor('Failed'), round: true },
          { label: 'Waiting', value: 'Waiting', color: $mitigations.getMitigationStatusColor('Waiting'), round: true },
          {
            label: 'Inactive',
            value: 'Inactive',
            color: $mitigations.getMitigationStatusColor('Inactive'),
            round: true
          }
        ]
      },

      types: {
        name: 'types',
        label: 'Type',
        allowMultiSelect: true,
        defaultValue: this.defaultValues.types,
        options: $mitigations.mitigationTypeOptions
      },

      sources: {
        name: 'sources',
        label: 'Source',
        defaultValue: this.defaultValues.sources,
        allowMultiSelect: true,
        showCheckboxes: true,
        customField: <ToggleButtonGroup highlightedActive multi />,
        fieldStyle: { flexDirection: 'row', justifyContent: 'space-between' },
        options: [
          { label: 'Policy', value: 'auto' },
          { label: 'Manual', value: 'manual' }
        ]
      },

      control: {
        name: 'control',
        label: 'Control',
        defaultValue: this.defaultValues.control,
        allowMultiSelect: true,
        showCheckboxes: true,
        customField: <ToggleButtonGroup highlightedActive multi />,
        fieldStyle: { flexDirection: 'row', justifyContent: 'space-between' },
        options: [
          { label: 'Policy', value: 'auto' },
          { label: 'Manual', value: 'manual' }
        ]
      },

      mitigations: {
        name: 'mitigations',
        label: 'Mitigation ID',
        defaultValue: this.defaultValues.mitigations,
        customField: <InputGroup />
      },

      alarms: {
        name: 'alarms',
        label: 'Alert ID',
        customField: <InputGroup />,
        defaultValue: this.defaultValues.alarms
      },

      methodID: {
        name: 'methodID',
        label: 'Method',
        defaultValue: this.defaultValues.methodID,
        allowMultiSelect: true,
        placeholder: 'No methods selected',
        options: methodOptions
      },

      platformID: {
        name: 'platformID',
        label: 'Platform',
        defaultValue: this.defaultValues.platformID,
        allowMultiSelect: true,
        placeholder: 'No platforms selected',
        options: platformOptions
      },

      showTenants: {
        name: 'showTenants',
        defaultValue: this.defaultValues.showTenants,
        customField: <Switch switchLabel="Show Tenant Mitigations" />,
        hidden: $app.isSubtenant || $mkp.tenants.tenantAlertingOptions.length === 0
      },

      tenantID: {
        name: 'tenantID',
        label: 'Tenant',
        defaultValue: this.defaultValues.tenantID,
        allowMultiSelect: true,
        placeholder: 'No tenants selected',
        options: $mkp.tenants.tenantAlertingOptions,
        hidden: !showTenants
      },

      policies: {
        name: 'policies',
        options: $alerting.flowPolicyIdOptions,
        label: 'Policy',
        allowMultiSelect: true,
        defaultValue: this.defaultValues.policies,
        placeholder: 'No policies selected'
      },

      ipCidrPattern: {
        name: 'ipCidrPattern',
        label: 'Target Search',
        customField: <InputGroup />,
        defaultValue: this.defaultValues.ipCidrPattern
      },

      keyPartsSearch: {
        name: 'keyPartsSearch',
        customField: <InputGroup />,
        label: 'Dimension Value',
        defaultValue: this.defaultValues.keyPartsSearch
      },

      exactSearch: {
        name: 'exactSearch',
        defaultValue: this.defaultValues.exactSearch,
        customField: <Switch switchLabel="Exact Dimension Value Match" />
      }
    };

    if (filterValues) {
      Object.keys(filterValues).forEach((key) => {
        let defaultValue = filterValues[key];
        const filterField = filterFields[key];

        // set the default form values for all of the incoming query values and prop values
        if (filterField) {
          if (filterField.allowMultiSelect && !Array.isArray(defaultValue)) {
            defaultValue = [defaultValue];
          }

          if (key === 'lookback') {
            defaultValue = parseInt(defaultValue);
            // if we're only showing lookback (and not start/end dates) make sure we have a valid value for the lookback (at least 24hrs)
            if (lookbackOnly && defaultValue < DAY_IN_SECONDS) {
              defaultValue = DAY_IN_SECONDS;
            }
          }

          filterField.defaultValue = defaultValue;
        } else {
          // could not find a matching filter form field for the incoming value - check start and end dates
          if (key === 'startDate' && !lookbackOnly) {
            filterFields.daterange.startDefaultValue = moment(defaultValue);
            filterFields.lookback.defaultValue = ALL_TIME_LOOKBACK;
          }

          if (key === 'endDate' && !lookbackOnly) {
            filterFields.daterange.endDefaultValue = moment(defaultValue);
            filterFields.lookback.defaultValue = ALL_TIME_LOOKBACK;
          }
        }
      });
    }

    return Object.values(filterFields);
  }

  /**
   * Maps UI-friendly statuses and control filter to API-ready state values.
   * @param {string[]} statuses - Array of UI statuses (e.g., ['Active', 'Inactive'])
   * @param {string[]} controlFilter - Array of control values (e.g., ['manual'])
   * @returns {string[]|null} - Array of API states or null.
   */
  getApiStates(statuses, controlFilter) {
    if (!Array.isArray(statuses)) {
      return null;
    }

    let states = statuses.flatMap((status) => MITIGATION_UI_STATE_TO_API_STATES_MAP[status]);

    if (Array.isArray(controlFilter) && controlFilter.length === 1) {
      states = states.filter((state) => {
        const hasManualState = MITIGATION_MANUAL_UI_STATES.includes(state);
        return controlFilter[0] === 'manual' ? hasManualState : !hasManualState;
      });
    }

    return states;
  }

  handleFilterChange = (filters, form, formValues, fieldName) => {
    const { collection, onChange } = this.props;

    this.setState({ showTenants: !!filters.showTenants });

    // set lookback/start/end dates

    if (includesInactive(filters?.statuses) && filters.lookback === ALL_TIME_LOOKBACK) {
      if (fieldName === 'statuses') {
        filters.lookback = DAY_IN_SECONDS; // set to last 24hrs if inactives are included
      } else {
        // If statuses is [], or ['Inactive'], filter by all except inactive.
        filters.statuses =
          filters?.statuses?.length > 1
            ? filters.statuses.filter((s) => s !== 'Inactive')
            : ['Waiting', 'Failed', 'Active'];
      }
    }

    const states = this.getApiStates(filters.statuses, filters?.control || this.defaultValues.control);

    let mitigations = filters.mitigations || '';
    mitigations = mitigations.replace(/[^0-9]/g, '');
    const alarms = filters.alarms || '';

    // Clear tenant selections if disabling show tenants
    if (!filters.showTenants) {
      filters.tenantID = [];
    }

    if (onChange) {
      onChange(filters);
    }

    if (collection) {
      collection.setServerFilter({
        ...filters,
        states,
        mitigations,
        alarms
      });
    }
  };

  render() {
    const { filterValues, lookbackOnly, ...otherProps } = this.props;

    return (
      <AdminFilterSidebar
        {...otherProps}
        filterValues={filterValues}
        filterFields={this.filterFields}
        resetValues={this.resetValues}
        defaultValues={this.defaultValues}
        onFilterChange={this.handleFilterChange}
      />
    );
  }
}

export default MitigationFilterSidebar;
